(function (root) {
  'use strict';
  const normalize = value => String(value || '').trim().toLowerCase();
  const unique = codes => [...new Map(codes.filter(Boolean).map(code => [normalize(code), String(code).trim()])).values()];
  function applications(cart) {
    return [...(cart.cart_level_discount_applications || []), ...(cart.items || []).flatMap(item => (item.line_level_discount_allocations || []).map(allocation => allocation.discount_application))].filter(Boolean);
  }
  function codesIn(cart) {
    return unique([...(cart.discount_codes || []).map(entry => entry.code), ...applications(cart).filter(app => app.type === 'discount_code').map(app => app.code || app.title)]);
  }
  function isSelected(cart, code) {
    return codesIn(cart).some(value => normalize(value) === normalize(code));
  }
  function isApplied(cart, code) {
    return (cart.discount_codes || []).some(entry => normalize(entry.code) === normalize(code) && entry.applicable === true) || applications(cart).some(app => app.type === 'discount_code' && normalize(app.code || app.title) === normalize(code) && Number(app.total_allocated_amount || app.value || 0) > 0);
  }
  function activeDiscounts(cart) {
    const keys = applications(cart).map(app => JSON.stringify([app.type, normalize(app.code || app.title), app.target_type || '', app.value_type || '', String(app.value ?? '')]));
    (cart.discount_codes || []).filter(entry => entry.applicable === true).forEach(entry => keys.push('code:' + normalize(entry.code)));
    return new Set(keys);
  }
  function discountsChanged(before, after) {
    const current = activeDiscounts(after);
    return [...activeDiscounts(before)].some(key => !current.has(key));
  }
  function formatOfferDate(value) {
    const date = new Date(value);
    if (!Number.isFinite(date.getTime())) return '';
    return new Intl.DateTimeFormat('en-US', {timeZone:'America/Los_Angeles',year:'numeric',month:'short',day:'numeric',hour:'numeric',minute:'2-digit',timeZoneName:'short'}).format(date);
  }
  async function request(fetcher, url, body, signal = AbortSignal.timeout(12000)) {
    const response = await fetcher(url, {method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, ...(body ? {body: JSON.stringify(body)} : {}), signal});
    if (!response.ok) {const error=new Error('cart_request_failed');error.status=response.status;throw error;}
    const cart = await response.json();
    if (!Array.isArray(cart.items)) throw new Error('cart_response_invalid');
    return cart;
  }
  async function changeCode(fetcher, base, code, sections, removing) {
    if (!code || code.includes(',')) throw new Error('invalid_code');
    const signal = AbortSignal.timeout(12000);
    const before = await request(fetcher, base + 'cart.js', undefined, signal);
    const existing = codesIn(before);
    const body = {discount: (removing ? existing.filter(value => normalize(value) !== normalize(code)) : unique([...existing, code])).join(',')};
    if (sections.length) body.sections = sections;
    let cart;
    try {cart = await request(fetcher, base + 'cart/update.js', body, signal);}
    catch(error) {error.updateAttempted=true;error.before=before;throw error;}
    const selected=isSelected(cart,code),applied=isApplied(cart,code);
    return {before, cart, selected, applied, removed: !selected, changed: discountsChanged(before, cart), pending: selected&&!applied};
  }
  const applyCode = (fetcher, base, code, sections) => changeCode(fetcher, base, code, sections, false);
  const removeCode = (fetcher, base, code, sections) => changeCode(fetcher, base, code, sections, true);
  function timeWindow(dataset, now) {
    const start=dataset.start ? Date.parse(dataset.start) : null;
    const end=dataset.end ? Date.parse(dataset.end) : null;
    return {hidden:(start!==null&&(!Number.isFinite(start)||now<start))||(end!==null&&(!Number.isFinite(end)||now>=end)),next:Math.min(...[start,end].filter(value=>Number.isFinite(value)&&value>now),Infinity)};
  }
  const api = {normalize, unique, applications, codesIn, isSelected, isApplied, activeDiscounts, discountsChanged, formatOfferDate, applyCode, removeCode, timeWindow};
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (!root.customElements || root.customElements.get('lr-product-offers')) return;
  let cartQueue = Promise.resolve();
  class ProductOffers extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.connectionRevision=(this.connectionRevision || 0)+1;
      this.busy = false;
      this.cartReady = false;
      this.syncing = false;
      this.pendingCode = null;
      this.pendingSections = null;
      this.reconcileAction = null;
      this.errors = new Map();
      this.cartRevision = 0;
      this.base = (this.dataset.root || '/').replace(/\/?$/, '/');
      this.text = JSON.parse(this.querySelector('[data-offer-messages]').textContent);
      this.status = this.querySelector('[data-offer-status]');
      this.querySelectorAll('[data-offer-end]').forEach(node => {node.textContent=formatOfferDate(node.dateTime);});
      const serverNow=Number(this.dataset.now)*1000;
      this.clockOffset=Number.isFinite(serverNow)&&serverNow>0 ? serverNow-root.performance.now() : null;
      this.addEventListener('click', event => this.onClick(event), {signal: this.controller.signal});
      this.querySelectorAll('dialog').forEach(dialog => {
        dialog.addEventListener('click', event => {if (event.target === dialog) {const box=dialog.getBoundingClientRect(); if(event.clientX<box.left||event.clientX>box.right||event.clientY<box.top||event.clientY>box.bottom) dialog.close();}}, {signal:this.controller.signal});
      });
      ['cart:updated', 'cart:refresh', 'ajaxProduct:added'].forEach(name => document.addEventListener(name, event => this.onCartUpdate(event.detail), {signal:this.controller.signal}));
      ['pageshow','focus'].forEach(name=>root.addEventListener(name,()=>this.scheduleRefresh(),{signal:this.controller.signal}));
      ['pageshow','focus'].forEach(name=>root.addEventListener(name,()=>this.syncVisibility(),{signal:this.controller.signal}));
      document.addEventListener('visibilitychange',()=>this.syncVisibility(),{signal:this.controller.signal});
      this.scope=this.closest('.product__info-container') || this.parentElement;
      this.scope.addEventListener('change',()=>this.syncVisibility(),{signal:this.controller.signal});
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, event => this.onCartUpdate(event));
        if(PUB_SUB_EVENTS.variantChange)this.unsubscribeVariant=subscribe(PUB_SUB_EVENTS.variantChange,event=>{if(event?.data?.sectionId===this.dataset.section)this.syncVisibility(event.data.variant?.id);});
      }
      this.syncVisibility();
      this.refresh();
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      this.connectionRevision++;
      this.refreshRevision=(this.refreshRevision || 0)+1;
      this.unsubscribeCart?.(); this.unsubscribeVariant?.();
      clearTimeout(this.timer); clearTimeout(this.refreshTimer);
      this.querySelectorAll('dialog[open]').forEach(dialog => dialog.close());
    }
    now() {return this.clockOffset===null ? Date.now() : this.clockOffset+root.performance.now();}
    syncVisibility(variantId) {
      const selected=String(variantId || this.scope.querySelector('form[action*="/cart/add"] [name="id"]')?.value || this.dataset.variant);
      const subscription=Boolean(this.scope.querySelector('form[action*="/cart/add"] [name="selling_plan"]')?.value);
      const now = this.now();
      let next=Infinity;
      this.querySelectorAll('[data-offer]').forEach(offer => {
        const variants=(offer.dataset.variants || '').split(',').map(id=>id.trim()).filter(Boolean);
        const window=timeWindow(offer.dataset,now);
        next=Math.min(next,window.next);
        offer.hidden = (variants.length>0&&!variants.includes(selected)) || (subscription&&offer.dataset.oneTimeOnly==='true') || window.hidden;
        const dialog=this.querySelector('#'+CSS.escape(offer.dataset.dialog));
        if (offer.hidden && dialog?.open) dialog.close();
      });
      this.hidden = !this.querySelector('[data-offer]:not([hidden])');
      clearTimeout(this.timer);
      this.timer=setTimeout(()=>this.syncVisibility(),Math.max(1,Math.min(30000,next-now+10)));
    }
    async onClick(event) {
      this.syncVisibility();
      if(event.target.closest('[data-offer]')?.hidden)return;
      const details = event.target.closest('[data-offer-details], [data-offer-error]');
      if (details) {const dialog=this.querySelector('#'+CSS.escape(details.getAttribute('aria-controls')));if(dialog&&!dialog.open)dialog.showModal();return;}
      const close=event.target.closest('[data-offer-close]');
      if (close) {close.closest('dialog').close();return;}
      const retry=event.target.closest('[data-offer-retry]');
      if(retry){
        const code=retry.dataset.offerRetry,issue=this.errors.get(normalize(code));
        if(!issue||this.busy||this.pendingCode||this.syncing)return;
        if(!this.cartReady||issue.action==='sync'){await this.refresh();return;}
        await this.changeOffer(code,issue.action==='remove',retry);return;
      }
      const button=event.target.closest('[data-offer-apply]');
      const removing=button?.type==='checkbox'&&button.dataset.selected==='true';
      if(button?.type==='checkbox')button.checked=button.dataset.selected==='true';
      if (!button || !this.cartReady || this.busy || this.pendingCode) return;
      await this.changeOffer(button.dataset.offerApply,removing,button);
    }
    async changeOffer(code,removing,button) {
      this.busy=true;
      this.refreshRevision=(this.refreshRevision || 0)+1;
      this.syncing=false;
      this.errors.delete(normalize(code));
      this.lastCode=code;
      this.lastAction=removing?'remove':'apply';
      this.applyDialog=removing?null:button.closest('dialog');
      this.updateControls();
      this.announce(removing?'':this.text.applying,code);
      const mini=document.querySelector('mini-cart');
      const sectionDefs=typeof mini?.getSectionsToRender === 'function' ? mini.getSectionsToRender().slice(0,5) : [];
      const connection=this.connectionRevision;
      let requestRevision;
      cartQueue = cartQueue.catch(()=>{}).then(()=>{
        if(!this.isConnected || connection!==this.connectionRevision)throw new Error('offer_disconnected');
        this.syncVisibility();
        if(button.closest('[data-offer]')?.hidden)throw new Error('offer_ended');
        requestRevision=this.cartRevision;
        return (removing?removeCode:applyCode)(root.fetch.bind(root),this.base,code,sectionDefs.map(item=>item.section || item.id));
      });
      try {
        const result=await cartQueue;
        if (!this.isConnected || connection!==this.connectionRevision) return;
        this.lastBeforeCart=result.before;
        if (requestRevision !== this.cartRevision) {
          this.pendingSections=sectionDefs;
          this.pendingCode=code;
          this.reconcileAction={code,removing};
          this.cartReady=false;
          this.lastResult=null;
          return;
        }
        this.lastResult=removing?(result.removed?'removed':'remove-failed'):result.selected?'selected':'ineligible';
        if(removing?!result.removed:!result.selected)this.setError(code,removing?'removeError':'applyError',removing?'remove':'apply');
        this.renderCart(result.cart);
        this.renderSections(result.cart.sections,sectionDefs);
        this.announce(removing ? (result.removed?'':this.text.error) : result.applied ? (result.changed ? this.text.appliedChanged : this.text.success) : result.pending ? this.text.pending : this.text.ineligible,code);
        document.dispatchEvent(new CustomEvent('cart:updated',{detail:{cart:result.cart,source:'product-offers'}}));
        if(typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate,{source:'product-offers',cartData:result.cart});
      } catch(error) {
        if(!this.isConnected || connection!==this.connectionRevision)return;
        if(error.updateAttempted){
          this.lastBeforeCart=error.before;
          this.pendingSections=sectionDefs;
          this.pendingCode=code;
          this.reconcileAction={code,removing};
          this.cartReady=false;
          this.lastResult=null;
          this.announce(this.text.syncing,code);
        } else {
          if(error.message!=='offer_ended')this.cartReady=false;
          this.setError(code,error.status===429?'rateLimit':'loadError','sync');
          this.announce(this.errorMessage(code),code);
        }
      } finally {
        if(connection===this.connectionRevision){
          this.busy=false;
          this.updateControls();
          if(this.pendingCode || this.pendingSections)this.scheduleRefresh();
        }
      }
    }
    onCartUpdate(detail) {
      if(detail?.source!=='product-offers')this.cartRevision++;
      this.scheduleRefresh();
    }
    renderSections(sections,definitions) {
      definitions.forEach(def => {
        const html=sections?.[def.section || def.id];
        const target=document.getElementById(def.id);
        if(typeof html!=='string'||!target)return;
        const content=new DOMParser().parseFromString(html,'text/html').querySelector(def.selector || '.shopify-section');
        if(content)target.innerHTML=content.innerHTML;
      });
    }
    announce(message,code=this.lastCode) {
      this.status.textContent=message;
      this.querySelectorAll('[data-dialog-status]').forEach(node=>{node.textContent=normalize(node.closest('dialog').querySelector('[data-offer-apply]')?.dataset.offerApply)===normalize(code) ? message : '';});
    }
    setError(code,reason,action) {this.errors.set(normalize(code),{reason,action});}
    errorMessage(code) {const issue=this.errors.get(normalize(code));return issue ? (this.text[issue.reason] || this.text.error) : '';}
    updateControls() {
      const waiting=this.busy || Boolean(this.pendingCode);
      const locked=waiting || !this.cartReady;
      this.setAttribute?.('aria-busy',String(waiting || this.syncing&&!this.cartReady));
      this.querySelectorAll('[data-offer-apply]').forEach(item=>{
        const active=normalize(item.dataset.offerApply)===normalize(this.pendingCode || this.lastCode);
        const pending=waiting?active:!this.cartReady&&this.syncing;
        const error=this.errors.has(normalize(item.dataset.offerApply));
        item.disabled=locked || (item.type!=='checkbox'&&item.dataset.selected==='true');
        item.indeterminate=!this.cartReady;
        item.dataset.unknown=String(!this.cartReady);
        item.dataset.pending=String(pending);
        item.dataset.blocked=String(locked&&!pending);
        item.dataset.error=String(error);
        item.setAttribute?.('aria-busy',String(pending));
        item.setAttribute?.('aria-invalid',String(error));
      });
      this.querySelectorAll('[data-offer-error]').forEach(button=>{
        button.hidden=!this.errors.has(normalize(button.dataset.offerError));
      });
      this.querySelectorAll('[data-offer-error-panel]').forEach(panel=>{
        const code=panel.dataset.offerErrorPanel,issue=this.errors.get(normalize(code));
        panel.hidden=!issue;
        panel.querySelector('[data-offer-error-message]').textContent=this.errorMessage(code);
        const retry=panel.querySelector('[data-offer-retry]');
        retry.textContent=!this.cartReady||issue?.action==='sync'?this.text.checkAgain:this.text.retry;
        retry.disabled=waiting||this.syncing;
      });
    }
    renderCart(cart) {
      this.cartReady=true;
      this.querySelectorAll('[data-offer-apply]').forEach(button=>{
        const applied=isApplied(cart,button.dataset.offerApply);
        const selected=isSelected(cart,button.dataset.offerApply);
        button.dataset.applied=String(applied);
        button.dataset.selected=String(selected);
        if(button.type==='checkbox')button.checked=selected;
        else button.textContent=selected ? this.text.applied : this.text.apply;
      });
      for(const [code,issue] of this.errors){
        if(issue.action==='sync'||isSelected(cart,code)===(issue.action==='apply'))this.errors.delete(code);
      }
      this.updateControls();
      if(this.applyDialog?.open&&isSelected(cart,this.lastCode)){
        this.applyDialog.close();
        this.applyDialog=null;
      }
    }
    scheduleRefresh() {clearTimeout(this.refreshTimer);this.refreshTimer=setTimeout(()=>this.refresh(),180);}
    async refresh() {
      if(this.busy || !this.isConnected) return;
      const revision=this.refreshRevision=(this.refreshRevision || 0)+1;
      const cartRevision=this.cartRevision;
      const connection=this.connectionRevision;
      const current=()=>!this.busy&&revision===this.refreshRevision&&cartRevision===this.cartRevision&&this.isConnected&&connection===this.connectionRevision;
      this.syncing=true;
      this.updateControls();
      try {
        const cart=await request(root.fetch.bind(root),this.base+'cart.js',undefined,AbortSignal.timeout(this.reconcileAction?6000:12000));
        if(current()){
          this.syncing=false;
          this.pendingCode=null;
          this.renderCart(cart);
          if(this.reconcileAction){
            const {code,removing}=this.reconcileAction;
            if(isSelected(cart,code)===removing)this.setError(code,removing?'removeError':'applyError',removing?'remove':'apply');
            else this.errors.delete(normalize(code));
            this.reconcileAction=null;
          }
          this.updateControls();
          if(this.lastCode){
            const applied=isApplied(cart,this.lastCode);
            const selected=isSelected(cart,this.lastCode);
            const result=this.lastAction==='remove'?(!selected?'removed':'remove-failed'):selected?'selected':'ineligible';
            if(result!==this.lastResult)this.announce(result==='removed'?'':result==='remove-failed'?this.text.error:result==='selected'?(applied?(this.lastBeforeCart&&discountsChanged(this.lastBeforeCart,cart)?this.text.appliedChanged:this.text.success):this.text.pending):this.text.ineligible);
            this.lastResult=result;
          }
          if(this.pendingSections){
            const definitions=this.pendingSections;
            const ids=[...new Set(definitions.map(def=>def.section||def.id))];
            if(!ids.length){this.pendingSections=null;return;}
            const response=await root.fetch(this.base+'?sections='+encodeURIComponent(ids.join(',')),{credentials:'same-origin',cache:'no-store',signal:AbortSignal.timeout(12000)});
            if(!response.ok)throw new Error('cart_sections_failed');
            const sections=await response.json();
            if(current()){
              this.renderSections(sections,definitions);
              this.pendingSections=null;
            }
          }
        }
      } catch(error) {
        if(current()){
          this.syncing=false;
          this.pendingCode=null;
          if(!this.cartReady){
            const code=this.reconcileAction?.code || this.lastCode;
            if(code)this.setError(code,error.status===429?'rateLimit':this.reconcileAction?'confirmError':'loadError',this.reconcileAction?(this.reconcileAction.removing?'remove':'apply'):'sync');
            else this.querySelectorAll('[data-offer-apply]').forEach(item=>this.setError(item.dataset.offerApply,error.status===429?'rateLimit':'loadError','sync'));
            this.announce(this.errorMessage(code || this.querySelector('[data-offer-apply]')?.dataset.offerApply));
          } else if(this.pendingSections)this.announce(this.text.error);
          this.updateControls();
        }
      } finally {
        if(revision===this.refreshRevision&&connection===this.connectionRevision){
          this.syncing=false;
          this.updateControls();
          if(cartRevision!==this.cartRevision&&!this.busy&&this.isConnected)this.scheduleRefresh();
        }
      }
    }
  }
  root.customElements.define('lr-product-offers',ProductOffers);
})(typeof window === 'undefined' ? globalThis : window);
